import { useEffect, useState, Suspense, useRef, useMemo, useCallback } from 'react';
import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { Bounds, OrbitControls, useGLTF, Html } from '@react-three/drei';
import * as THREE from 'three';
import useCache, { IMAGE_KEY } from "../../hooks/useCache";
import { ViewerControls } from './ViewerControls';
import { DraggableAxisWindow } from './DraggableAxisWindow';
import { Model } from './sceneModel';
import { CircularProgress } from "@mui/material";
import { Leva, useControls, button, folder } from 'leva';


export interface ViewerWithUrlsProps {  
  analysisID: string;
  image: IMAGE_KEY;
}

export default function Viewer3D(props: ViewerWithUrlsProps) {
  const [url, setUrl] = useState<string | null>(null);
  const { getPresignedUrl: rawGetPresignedUrl } = useCache();
  const getPresignedUrl = useCallback(rawGetPresignedUrl, []);

  const sceneRef = useRef<THREE.Scene | null>(null);
  
  const displayControls = useControls("Display", {
    wireframe: { value: false },
    grid: { value: false },
    axes: { value: false },
    bgColor: "#ffffff",
  });

  useEffect(() => {
    if (sceneRef.current) {
      sceneRef.current.background = new THREE.Color(displayControls.bgColor);
    }
  }, [displayControls.bgColor]);

  const [rotation, setRotation] = useState({
    rotateX: 0,
    rotateY: 0,
    rotateZ: 0,
  });

  const [rotationControls, setRotationControls] = useControls("Rotation", () => ({
    rotateX: {
      value: 0,
      min: -180,
      max: 180,
      step: 1,
      onChange: (v) => setRotation((r) => ({ ...r, rotateX: v })),
    },
    rotateY: {
      value: 0,
      min: -180,
      max: 180,
      step: 1,
      onChange: (v) => setRotation((r) => ({ ...r, rotateY: v })),
    },
    rotateZ: {
      value: 0,
      min: -180,
      max: 180,
      step: 1,
      onChange: (v) => setRotation((r) => ({ ...r, rotateZ: v })),
    },
    "Reset Rotation": button(() => {
      setRotation({ rotateX: 0, rotateY: 0, rotateZ: 0 });
      setRotationControls({ rotateX: 0, rotateY: 0, rotateZ: 0 });
    }),
  }));
  
  useEffect(() => {
    let mounted = true;
    (async () => {
      const fetchedUrl = await getPresignedUrl(props.analysisID, props.image);
      if (mounted) {
        setUrl((prev) => (prev !== fetchedUrl ? fetchedUrl : prev)); // don't update if same url
      }
    })();

    return () => {
      mounted = false;
    };
  }, [props.analysisID, props.image]);

  if (!url) {
    return (
      <div
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: displayControls.bgColor,
          zIndex: 10,
        }}
      >
        <CircularProgress size={40} />
      </div>
    );
  }
  
  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: "100%",
        height: "100%",
        border: "none",
        backgroundColor: displayControls.bgColor,
        cursor: "grab",
      }}
      onMouseDown={(e) => (e.currentTarget.style.cursor = "grabbing")}
      onMouseUp={(e) => (e.currentTarget.style.cursor = "grab")}
    >
    <Canvas
      camera={{ position: [0, 0, 10], near: 1 }}
      gl={{ preserveDrawingBuffer: true }}
      onCreated={({ scene }) => {
        scene.background = new THREE.Color(displayControls.bgColor);
        sceneRef.current = scene;
      }}
    >
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 10, 10]} />
      <Suspense fallback={
        <Html center>
          <CircularProgress size={40} />
        </Html>
      }>
        <Bounds fit clip margin={1.2}>
          <Model
            url={url}
            wireframe={displayControls.wireframe}
            grid={displayControls.grid}
            axes={displayControls.axes}
            rotation={rotation}
          />
        </Bounds>
      </Suspense>
      <OrbitControls
        enablePan
        enableZoom
        enableRotate
        screenSpacePanning
        target={[0, 0, 0]}
        panSpeed={1}
        rotateSpeed={0.8}
        mouseButtons={{
          LEFT: THREE.MOUSE.ROTATE,
          MIDDLE: THREE.MOUSE.PAN,
          RIGHT: THREE.MOUSE.PAN,
        }}
      />
      <ViewerControls />
    </Canvas>
    {displayControls.axes && <DraggableAxisWindow rotation={ rotation } />}
    <Leva collapsed={false}
      titleBar={{ title: "3D Viewer Controls", position: { x: -60, y: 100 } }}
      hideCopyButton 
    />
    </div>
  );
}